import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface BankAccount {
  id: string;
  display_name: string;
  bank_code: string | null;
  branch_code: string | null;
  account_kind: string | null;
  account_number: string | null;
  currency: string;
  status: string;
}

export interface BankTx {
  id: string;
  bank_account_id: string;
  posted_on: string;
  amount_cents: string;
  currency: string;
  description: string | null;
  counterparty: string | null;
  matched_kind: string | null;
}

export type JpBank = 'mizuho' | 'smbc' | 'mufg' | 'rakuten' | 'paypay' | 'universal';

export function useBankAccounts(wsId: string | undefined) {
  return useQuery<BankAccount[]>({
    queryKey: ['bank-accounts', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<BankAccount[]>(`/workspaces/${wsId}/bank-feed/accounts`)).data,
  });
}

export function useBankTransactions(wsId: string | undefined, accountId?: string) {
  return useQuery<BankTx[]>({
    queryKey: ['bank-tx', wsId, accountId ?? ''],
    enabled: !!wsId,
    queryFn: async () => {
      const url = accountId
        ? `/workspaces/${wsId}/bank-feed/transactions?bankAccountId=${accountId}`
        : `/workspaces/${wsId}/bank-feed/transactions`;
      return (await api.get<BankTx[]>(url)).data;
    },
  });
}

export interface MatchSuggestion {
  id: string;
  matchedKind: 'ap_invoice' | 'ar_invoice';
  score: number;
  partyName: string | null;
  firstTimePartner: boolean;
}

/** Suggested AP/AR matches for one unmatched bank transaction (F8). */
export function useMatchSuggestions(wsId: string | undefined, txId: string | null) {
  return useQuery<MatchSuggestion[]>({
    queryKey: ['bank-tx-suggestions', wsId, txId],
    enabled: !!wsId && !!txId,
    queryFn: async () =>
      (await api.get<MatchSuggestion[]>(`/workspaces/${wsId}/bank-feed/transactions/${txId}/suggestions`)).data,
  });
}

export function useCreateBankAccount(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      displayName: string;
      bankCode?: string;
      branchCode?: string;
      accountKind?: string;
      accountNumber?: string;
      currency: string;
    }) => (await api.post<BankAccount>(`/workspaces/${wsId}/bank-feed/accounts`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-accounts', wsId] }),
  });
}

/** Universal CSV — JSON body, no file upload. */
export function useImportUniversalCsv(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bankAccountId, csv, currency }: { bankAccountId: string; csv: string; currency: string }) =>
      (
        await api.post<{ imported: number }>(`/workspaces/${wsId}/bank-feed/import`, {
          bankAccountId,
          csv,
          currency,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-tx', wsId] }),
  });
}

/** JP-bank-specific upload — multipart. Server-side handles Shift_JIS
 *  for Mizuho/MUFG and column mapping per bank. */
export function useImportJpBankStatement(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bank,
      bankAccountId,
      currency,
      file,
    }: {
      bank: Exclude<JpBank, 'universal'>;
      bankAccountId: string;
      currency: string;
      file: File;
    }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('bank', bank);
      form.append('bankAccountId', bankAccountId);
      form.append('currency', currency);
      return (
        await api.post<{ imported: number }>(`/workspaces/${wsId}/connectors/jp-banks/import`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      ).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-tx', wsId] }),
  });
}

export function useAutoMatch(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ matched: number }>(`/workspaces/${wsId}/bank-feed/auto-match`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-tx', wsId] }),
  });
}
